import type { StringChannels } from "@shared/schema";
import { Tooltip } from "../ui/tooltip";
import { cn } from "@/lib/utils";

/**
 * Per-channel stacked mini-bars (CHAN-01..04). Each channel renders as a thin
 * coloured segment whose height reflects its raw 0–1 STRING score. Channels
 * with score 0 collapse to a faint grey tick so the user can still see they
 * were considered. Hovering the whole block reveals a "not biologically
 * equivalent" caveat — we intentionally never sum these into a single number
 * on the table surface (INT-01).
 */

export interface ChannelBarsProps {
  channels: StringChannels;
  className?: string;
  compact?: boolean;
}

const ORDER: Array<{
  key: keyof StringChannels;
  label: string;
  color: string;
}> = [
  { key: "experimental", label: "Experimental", color: "hsl(var(--channel-experimental))" },
  { key: "database", label: "Curated DB", color: "hsl(var(--channel-database))" },
  { key: "textmining", label: "Text mining", color: "hsl(var(--channel-textmining))" },
  { key: "coexpression", label: "Co-expression", color: "hsl(var(--channel-coexpression))" },
  { key: "neighborhood", label: "Neighborhood", color: "hsl(var(--channel-neighborhood))" },
  { key: "fusion", label: "Fusion", color: "hsl(var(--channel-fusion))" },
  { key: "cooccurrence", label: "Co-occurrence", color: "hsl(var(--channel-cooccurrence))" },
];

function displayScore(v: number | undefined): number {
  if (v == null) return 0;
  return Math.round(Math.max(0, Math.min(1, v)) * 1000);
}

export function ChannelBars({ channels, className, compact = false }: ChannelBarsProps) {
  const height = compact ? 16 : 20;
  const width = compact ? 5 : 6;
  return (
    <Tooltip
      className="max-w-[260px] text-left"
      content={
        <div className="space-y-1">
          <div className="font-semibold">Active STRING channels</div>
          <ul className="space-y-0.5 text-[11px]">
            {ORDER.map((c) => {
              const v = channels[c.key] as number | undefined;
              if (c.key === "combined") return null;
              return (
                <li key={c.key} className="flex items-center justify-between gap-3">
                  <span className="flex items-center gap-1.5">
                    <span
                      className="inline-block h-2 w-2 rounded-sm"
                      style={{ backgroundColor: c.color }}
                    />
                    {c.label}
                  </span>
                  <span className="font-mono">{displayScore(v)}</span>
                </li>
              );
            })}
          </ul>
          <p className="text-[10px] text-muted-foreground">
            Channels are <strong>not biologically equivalent</strong> — experimental & curated DB
            evidence reflect direct binding; text mining and co-expression do not.
          </p>
        </div>
      }
    >
      <span
        className={cn("inline-flex items-end gap-[2px]", className)}
        style={{ height }}
        aria-label="STRING channel contributions"
      >
        {ORDER.filter((c) => c.key !== "combined").map((c) => {
          const v = (channels[c.key] as number | undefined) ?? 0;
          const h = Math.max(v, 0.02) * height;
          return (
            <span
              key={c.key}
              style={{
                width,
                height: h,
                backgroundColor: v > 0 ? c.color : "hsl(var(--muted-foreground) / 0.25)",
              }}
              className="rounded-sm"
            />
          );
        })}
      </span>
    </Tooltip>
  );
}
